/**
 * Auto-detect whether the user's selected microphone is already being
 * cleaned by an OS-level denoiser (Krisp / NVIDIA Broadcast / etc.).
 *
 * Layering our in-app denoiser (DFN / RNNoise) on top of an OS denoiser
 * is actively harmful — both apply gain ducking / spectral subtraction,
 * the second pass eats consonants and creates a "watery" artefact. So
 * if we detect a known virtual mic, we publish the raw track unchanged
 * and let the user's existing tool do its job.
 *
 * Detection is by device label substring. The browser exposes the
 * virtual mic's label once the user has granted mic permission. Pre-
 * permission labels are redacted (empty string) — we just treat that
 * as "not detected" and fall through to our normal cascade. The next
 * mic op after first capture will re-detect with the real label.
 *
 * Limitations:
 *   • Mac/iOS native Voice Isolation processes inline on the system
 *     mic without changing its label — we cannot detect it. Users who
 *     enable it system-wide will get double-processing from our DFN.
 *     Counter-measure: the user can disable our in-app NS via the
 *     existing settings toggle (localStorage `noiseSuppression`).
 *   • Detection is name-based, so a future product renamed at
 *     marketing's whim will silently slip through until we add it
 *     here. Low cost to maintain since the list is short.
 */

/**
 * The OS-level denoiser brand we identified on the user's mic, or
 * null if none was recognised. Returned brands map 1:1 to the
 * `DenoiserStatus` values used by the picker badge so the badge can
 * show "Krisp" / "NVIDIA Broadcast" instead of our own backends.
 */
export type OsDenoiserBrand = 'krisp' | 'nvidia' | null

/** Known device-label substrings (lower-cased) per brand. */
const KNOWN_OS_DENOISERS: Array<{ brand: Exclude<OsDenoiserBrand, null>; patterns: RegExp[] }> = [
  {
    brand: 'krisp',
    // Krisp ships a virtual mic named "Krisp Microphone" (Win) /
    // "Krisp Audio" (Mac). The "krisp" substring is unique to their
    // product — no false positives we've seen.
    patterns: [/krisp/i],
  },
  {
    brand: 'nvidia',
    // NVIDIA Broadcast exposes "Microphone (NVIDIA Broadcast)" or
    // "NVIDIA Broadcast Microphone". RTX Voice (older product, now
    // folded into Broadcast) used "NVIDIA RTX Voice" — keep both for
    // legacy installs.
    patterns: [/nvidia\s*broadcast/i, /nvidia\s*rtx\s*voice/i],
  },
]

/**
 * Detect from a live MediaStreamTrack's label whether the underlying
 * mic is an OS-level denoiser's virtual device.
 *
 * @param track The raw audio track returned by getUserMedia. Pass the
 *              track AFTER the user has granted permission — pre-grant
 *              labels are empty strings and will always return null.
 */
export function detectOsDenoiserFromTrack(track: MediaStreamTrack | null | undefined): OsDenoiserBrand {
  const label = (track?.label ?? '').trim()
  if (!label) return null
  return matchLabel(label)
}

/**
 * Detect from an arbitrary device label string. Exposed for the
 * settings UI ("your mic is Krisp Microphone — we'll skip in-app NS")
 * and for tests.
 */
export function detectOsDenoiserFromLabel(label: string): OsDenoiserBrand {
  return matchLabel(label.trim())
}

function matchLabel(label: string): OsDenoiserBrand {
  if (!label) return null
  for (const entry of KNOWN_OS_DENOISERS) {
    if (entry.patterns.some((p) => p.test(label))) return entry.brand
  }
  return null
}
